import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  AlertTriangle, Heart, MapPin, Users, Home, Droplets, 
  Stethoscope, ArrowRight, Phone, Mail, Calendar, Target
} from 'lucide-react';
import { Button } from '../components/ui/button';

const PAYPAL_LINK = "https://www.paypal.com/donate/?hosted_button_id=ZUX3BKRN524VS";

const impactStats = [
  { value: '125', label: 'Lives Lost', icon: AlertTriangle },
  { value: '3,400+', label: 'People Displaced', icon: Users },
  { value: '680', label: 'Households Affected', icon: Home },
  { value: '600+', label: 'Homes Destroyed', icon: Home },
];

const urgentNeeds = [
  {
    icon: Home,
    title: 'Shelter & Non-Food Items',
    description: 'Emergency shelter kits, plastic sheeting, reinforced tents, thermal blankets, and kitchen sets for over 600 families with destroyed or unsafe homes.',
    color: '#E08B7A'
  },
  {
    icon: Target,
    title: 'Food Security & Nutrition',
    description: 'Three months of immediate food assistance including fortified cereals, pulses, and vegetable oil. Malnutrition screening for children under five and lactating mothers.',
    color: '#E5B75F'
  },
  {
    icon: Droplets,
    title: 'WASH (Water, Sanitation & Hygiene)',
    description: 'Water purification chemicals, collapsible water containers, and emergency latrines to prevent cholera and waterborne diseases.',
    color: '#7BC4E2'
  },
  {
    icon: Stethoscope,
    title: 'Health & Psychosocial Support',
    description: 'Mobile health clinics for basic medical care and dedicated psychosocial counselors for trauma-informed healing sessions.',
    color: '#4A7C59'
  }
];

const donationImpact = [
  { amount: 5000, impact: 'Provides emergency shelter and sanitation for 50 families' },
  { amount: 15000, impact: 'Funds a mobile health unit and clean water access for one month' },
  { amount: 50000, impact: 'Launches a Resilience Hub for long-term vocational retraining' },
];

export default function EmergencyAppealPage() {
  return (
    <div className="min-h-screen bg-white">
      {/* Urgent Banner */}
      <div className="bg-red-600 text-white py-3">
        <div className="container-custom flex items-center justify-center gap-3">
          <AlertTriangle className="w-5 h-5 animate-pulse" />
          <span className="font-bold">URGENT HUMANITARIAN APPEAL</span>
          <AlertTriangle className="w-5 h-5 animate-pulse" />
        </div>
      </div>

      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-red-900">
          <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-red-500/20 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-[#E5B75F]/10 rounded-full blur-3xl" />
        </div>
        
        <div className="container-custom relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="inline-flex items-center gap-2 bg-red-500/20 border border-red-500/40 rounded-full px-4 py-2 mb-6">
                <Calendar className="w-4 h-4 text-red-400" />
                <span className="text-red-300 text-sm font-medium">March 18, 2026 | OPAD East Africa</span>
              </div>
              
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold font-['Manrope'] text-white mb-6 leading-tight">
                Gamo Zone Landslide Response & Resilience Building in Southern Ethiopia
              </h1>
              
              <p className="text-xl text-slate-300 mb-8 max-w-3xl mx-auto">
                A catastrophic series of landslides and floods triggered by unprecedented torrential rainfall 
                has devastated communities in the Gamo Zone. Immediate humanitarian assistance is urgently needed.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a href={PAYPAL_LINK} target="_blank" rel="noopener noreferrer">
                  <Button size="lg" className="bg-red-600 hover:bg-red-700 text-white rounded-full px-10 py-6 text-lg font-semibold shadow-lg">
                    <Heart className="w-5 h-5 mr-2" />
                    Donate Now - Save Lives
                  </Button>
                </a>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Impact Statistics */}
      <section className="py-12 bg-slate-900">
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {impactStats.map((stat, index) => (
              <motion.div
                key={stat.label}
                className="text-center"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <stat.icon className="w-8 h-8 text-red-500 mx-auto mb-2" />
                <div className="text-3xl md:text-4xl font-bold text-white mb-1">{stat.value}</div>
                <div className="text-slate-400 text-sm">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Situation Overview */}
      <section className="py-16 bg-[#FDF8F4]">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-2xl md:text-3xl font-bold font-['Manrope'] text-slate-900 mb-6">
                The Situation
              </h2>
              <div className="prose prose-lg max-w-none text-slate-600">
                <p className="mb-4">
                  On <strong>March 10, 2026</strong>, unprecedented torrential rainfall triggered catastrophic 
                  landslides and floods across the <strong>Gamo Zone, South Ethiopia Region</strong>, specifically 
                  in the Woredas of Gacho Baba, Bonke, Kamba Zuria, and West Abaya.
                </p>
                <p className="mb-4">
                  The disaster has resulted in <strong>125 confirmed fatalities</strong> (as of March 18, 2026), 
                  with <strong>3,400+ people displaced</strong> from their homes. Primary health posts, community 
                  schools, and vital access roads have been destroyed. Hundreds of hectares of subsistence crops 
                  have been lost along with significant livestock mortality.
                </p>
                <p>
                  Survivors are currently sheltered in makeshift camps, schools, and with host families. 
                  <strong> Ongoing rainfall poses a persistent threat</strong> of secondary landslides, 
                  complicating rescue efforts and aid delivery.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Location Map/Info */}
      <section className="py-12 bg-white border-y">
        <div className="container-custom">
          <div className="flex flex-wrap items-center justify-center gap-8">
            <div className="flex items-center gap-3">
              <MapPin className="w-6 h-6 text-red-500" />
              <div>
                <div className="font-bold text-slate-900">Location</div>
                <div className="text-slate-600">Gamo Zone, South Ethiopia Region</div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Calendar className="w-6 h-6 text-red-500" />
              <div>
                <div className="font-bold text-slate-900">Date of Incident</div>
                <div className="text-slate-600">March 10, 2026</div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Users className="w-6 h-6 text-red-500" />
              <div>
                <div className="font-bold text-slate-900">Affected Woredas</div>
                <div className="text-slate-600">Gacho Baba, Bonke, Kamba Zuria, West Abaya</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Urgent Needs */}
      <section className="py-16 bg-white">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold font-['Manrope'] text-slate-900 mb-4">
              Immediate Humanitarian Needs
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Your support will directly fund these critical life-saving interventions.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {urgentNeeds.map((need, index) => (
              <motion.div
                key={need.title}
                className="bg-[#FDF8F4] rounded-2xl p-6 border border-slate-100"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <div 
                  className="w-14 h-14 rounded-xl flex items-center justify-center mb-4"
                  style={{ backgroundColor: `${need.color}20` }}
                >
                  <need.icon className="w-7 h-7" style={{ color: need.color }} />
                </div>
                <h3 className="font-bold text-slate-900 text-lg mb-2">{need.title}</h3>
                <p className="text-slate-600">{need.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Donation Impact */}
      <section className="py-16 bg-gradient-to-br from-red-600 to-red-700">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold font-['Manrope'] text-white mb-4">
              Your Donation Makes a Direct Impact
            </h2>
            <p className="text-white/80 max-w-2xl mx-auto">
              See how your contribution can help save lives and rebuild communities.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {donationImpact.map((tier, index) => (
              <motion.div
                key={tier.amount}
                className="bg-white rounded-2xl p-6 text-center"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="text-4xl font-bold text-red-600 mb-3">${tier.amount.toLocaleString()}</div>
                <p className="text-slate-600">{tier.impact}</p>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-10">
            <a href={PAYPAL_LINK} target="_blank" rel="noopener noreferrer">
              <Button size="lg" className="bg-white text-red-600 hover:bg-white/90 rounded-full px-10 py-6 text-lg font-semibold shadow-lg">
                <Heart className="w-5 h-5 mr-2" />
                Donate Now
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Long-term Strategy */}
      <section className="py-16 bg-[#FDF8F4]">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold font-['Manrope'] text-slate-900 mb-6 text-center">
              Building Back Better: Long-Term Resilience Strategy
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              {[
                { title: 'Livelihood Restoration', desc: 'Providing seed capital and vocational training for diversified, non-farm economic activities.' },
                { title: 'Climate-Smart Infrastructure', desc: 'Supporting reconstruction with landslide-resistant engineering and improved land-use planning.' },
                { title: 'Environmental Mitigation', desc: 'Implementing community-led reforestation and terracing projects to stabilize soil and mitigate runoff.' },
                { title: 'Early Warning Systems', desc: 'Establishing Kebele-level disaster committees with communication tools for rainfall monitoring and timely evacuations.' },
              ].map((item, index) => (
                <div key={item.title} className="bg-white rounded-xl p-5 shadow-sm">
                  <h3 className="font-bold text-slate-900 mb-2">{item.title}</h3>
                  <p className="text-slate-600 text-sm">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Contact */}
      <section className="py-16 bg-white">
        <div className="container-custom">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-2xl md:text-3xl font-bold font-['Manrope'] text-slate-900 mb-6">
              Contact OPAD East Africa
            </h2>
            <div className="bg-slate-50 rounded-2xl p-8">
              <p className="text-slate-600 mb-4">
                <strong>Regional Director:</strong> Mesay Tadese
              </p>
              <div className="flex flex-col md:flex-row items-center justify-center gap-6">
                <a href="mailto:mesay@opad.eu" className="flex items-center gap-2 text-slate-700 hover:text-red-600">
                  <Mail className="w-5 h-5 text-red-500" />
                  mesay@opad.eu
                </a>
                <a href="tel:+251993429152" className="flex items-center gap-2 text-slate-700 hover:text-red-600">
                  <Phone className="w-5 h-5 text-red-500" />
                  +251 993 429 152
                </a>
              </div>
              <p className="text-slate-500 text-sm mt-4">
                Addis Ababa, Ethiopia | www.opad.eu
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-slate-900">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <AlertTriangle className="w-16 h-16 text-red-500 mx-auto mb-6" />
            <h2 className="text-3xl md:text-4xl font-bold font-['Manrope'] text-white mb-4">
              Time is Critical
            </h2>
            <p className="text-xl text-slate-300 mb-8 max-w-2xl mx-auto">
              Every moment counts. Your donation today can provide immediate relief to families 
              who have lost everything. Together, we can save lives and rebuild hope.
            </p>
            <a href={PAYPAL_LINK} target="_blank" rel="noopener noreferrer">
              <Button size="lg" className="bg-red-600 hover:bg-red-700 text-white rounded-full px-12 py-6 text-xl font-semibold shadow-lg">
                <Heart className="w-6 h-6 mr-2" />
                Donate Now - Save Lives
              </Button>
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
